import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TestimonialsCarouselTest = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef(null);

  const testimonials = [
    { name: "Isabel", role: "Mentoría individual", text: "Me llevo una conexión interna real. Ahora soy consciente de lo desconectada que estaba. Es un lugar de autonomía, de energía y de poder personal.", highlight: "Conexión interna real" },
    { name: "María", role: "Taller Hieros Gamos", text: "Por primera vez me hice consciente de que sentía amor, pero no hacia mí misma. Ahora llevo días sintiéndome como el sofá al lado de la chimenea… para mí.", highlight: "Autoacogida profunda" },
    { name: "Vanessa", role: "Taller Hieros Gamos", text: "La sensación ha sido la de haber dado un salto hacia adelante. Dejar de darle vueltas a la cabeza y volver al sentir.", highlight: "Menos mente, más presencia" },
    { name: "Elisenda", role: "Taller Hieros Gamos", text: "Mi mente, que siempre va al 200%, se ha parado por completo durante días. No observaba, no analizaba, simplemente estaba.", highlight: "Parada mental profunda" },
    { name: "Patricia", role: "Taller Hieros Gamos", text: "Todo lo que se trabajaba pasaba por el corazón, no por la mente. Cuando algo lo puedes explicar desde ahí, es que ya está integrado.", highlight: "Comprensión integradora" },
    { name: "Ana", role: "Taller Hieros Gamos", text: "He sentido muy claro tres lugares del planeta, con una conexión muy viva. Ha sido una sensación muy real de integración y cierre.", highlight: "Recuperación de energía" }
  ];

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goTo = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  return (
    <div className="pt-20 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="bg-gray-900 text-white py-6 px-6 text-center">
        <h1 className="text-2xl font-bold">Carrusel de Testimonios - 6 completos</h1>
      </div>

      {/* OPCIÓN 1: Carrusel con 1 testimonio grande */}
      <div className="py-12 px-4 bg-[#faf8f5]">
        <h2 className="text-xl font-bold text-center mb-8 text-[#c9a227]">
          OPCIÓN 1: Un testimonio grande a la vez (con navegación)
        </h2>
        
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-serif text-gray-800 mb-2">Voces del camino</h3>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto" />
          </div>
          
          {/* Carousel container */}
          <div className="relative">
            {/* Navigation arrows */}
            <button 
              onClick={goPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button 
              onClick={goNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>

            {/* Testimonial card */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((t, i) => (
                  <div key={i} className="w-full flex-shrink-0 px-4">
                    <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm text-center">
                      <Quote className="w-10 h-10 text-[#c9a227]/30 mx-auto mb-6" />
                      <p className="text-sm font-medium text-[#c9a227] mb-4">"{t.highlight}"</p>
                      <p className="text-lg lg:text-xl text-gray-600 italic leading-relaxed mb-8">
                        "{t.text}"
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#c9a227]/20" />
                        <div className="text-left">
                          <p className="font-medium text-gray-800">{t.name}</p>
                          <p className="text-sm text-gray-500">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex ? 'bg-[#c9a227] w-6' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* OPCIÓN 2: Carrusel horizontal con varios visibles */}
      <div className="py-12 px-4 bg-white">
        <h2 className="text-xl font-bold text-center mb-8 text-[#c9a227]">
          OPCIÓN 2: Carrusel con scroll horizontal (varios visibles)
        </h2>
        
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-serif text-gray-800 mb-2">Voces del camino</h3>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto" />
          </div>
          
          {/* Horizontal scroll carousel */}
          <div 
            ref={carouselRef}
            className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((t, i) => (
              <div 
                key={i} 
                className="flex-shrink-0 w-[320px] snap-center"
              >
                <div className="bg-[#faf8f5] rounded-2xl p-6 h-full">
                  <p className="text-sm font-medium text-[#c9a227] mb-3">"{t.highlight}"</p>
                  <p className="text-gray-600 text-sm italic leading-relaxed mb-6">
                    "{t.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#c9a227]/20" />
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Scroll hint */}
          <p className="text-center text-sm text-gray-400 mt-4">
            ← Desliza para ver más →
          </p>
        </div>
      </div>

      {/* OPCIÓN 3: Carrusel con cards más elegantes */}
      <div className="py-12 px-4 bg-[#faf8f5]">
        <h2 className="text-xl font-bold text-center mb-8 text-[#c9a227]">
          OPCIÓN 3: Carrusel elegante con flechas y auto-play
        </h2>
        
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-serif text-gray-800 mb-2">Voces del camino</h3>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto" />
          </div>
          
          {/* Main carousel */}
          <div className="relative px-12">
            {/* Arrows */}
            <button 
              onClick={goPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[#c9a227]/30 flex items-center justify-center hover:bg-[#c9a227]/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#c9a227]" />
            </button>
            <button 
              onClick={goNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[#c9a227]/30 flex items-center justify-center hover:bg-[#c9a227]/10 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#c9a227]" />
            </button>

            {/* Cards */}
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((t, i) => (
                  <div key={i} className="w-full flex-shrink-0">
                    <div className="bg-white p-10 lg:p-14 text-center">
                      <div className="w-16 h-16 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-6">
                        <Quote className="w-7 h-7 text-[#c9a227]" />
                      </div>
                      <p className="text-[#c9a227] font-medium mb-4">"{t.highlight}"</p>
                      <p className="text-xl text-gray-600 italic leading-relaxed max-w-2xl mx-auto mb-8">
                        "{t.text}"
                      </p>
                      <div className="inline-flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#c9a227]/30 to-[#c9a227]/10" />
                        <div className="text-left">
                          <p className="font-medium text-gray-800">{t.name}</p>
                          <p className="text-sm text-[#c9a227]">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === currentIndex ? 'bg-[#c9a227] w-8' : 'bg-gray-300 w-4'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="py-8 bg-gray-900 text-white text-center">
        <h2 className="text-xl font-bold mb-4">¿Cuál prefieres?</h2>
        <ul className="space-y-2 text-gray-300">
          <li>• <strong>Opción 1:</strong> Un testimonio grande con flechas</li>
          <li>• <strong>Opción 2:</strong> Scroll horizontal (varios visibles)</li>
          <li>• <strong>Opción 3:</strong> Elegante con auto-play y barra de progreso</li>
        </ul>
      </div>
    </div>
  );
};

export default TestimonialsCarouselTest;
